import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrowseTemplates = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Detect language from URL
  const getLangFromURL = () => {
    const path = location.pathname;
    if (path.includes('-ru')) return 'ru';
    if (path.includes('-kz')) return 'kz';
    return 'en';
  };

  useEffect(() => {
    const urlLang = getLangFromURL();
    if (i18n.language !== urlLang) {
      i18n.changeLanguage(urlLang);
    }
    fetchTemplates(urlLang);
  }, [location.pathname]);

  const fetchTemplates = async (lang = 'en') => {
    try {
      const response = await axios.get(`${API}/templates?lang=${lang}`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-12 h-12 text-[#1E88E5] animate-spin" />
        </div>
      </div>
    );
  }

  const handleTemplateClick = (templateId) => {
    const urlLang = getLangFromURL();
    const suffix = urlLang === 'en' ? '' : `-${urlLang}`;
    navigate(`/customize${suffix}/${templateId}`);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-block mb-4 md:mb-6">
            <span className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-full text-xs md:text-sm shadow-lg">
              ✨ {t('browse.featuredTemplates')}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 mb-4 md:mb-6 px-4">
            {t('browse.pageTitle')}
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            {t('browse.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {templates.map((template, index) => {
            // Create beautiful gradient backgrounds for each card
            const gradients = [
              'bg-gradient-to-br from-purple-400 via-pink-400 to-rose-400',
              'bg-gradient-to-br from-blue-400 via-cyan-400 to-teal-400',
              'bg-gradient-to-br from-amber-400 via-orange-400 to-red-400',
              'bg-gradient-to-br from-green-400 via-emerald-400 to-cyan-400',
              'bg-gradient-to-br from-indigo-400 via-purple-400 to-pink-400',
              'bg-gradient-to-br from-rose-400 via-red-400 to-orange-400',
            ];
            
            const gradientClass = gradients[index % gradients.length];
            
            return (
              <div
                key={template.id}
                className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:scale-105 cursor-pointer group"
                onClick={() => handleTemplateClick(template.id)}
                data-testid={`template-card-${template.name.toLowerCase().replace(' ', '-')}`}
              >
                <div className={`${gradientClass} p-8 md:p-12 text-center min-h-[280px] md:min-h-[320px] flex flex-col items-center justify-center relative overflow-hidden`}>
                  {/* Decorative circles */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
                  <div className="absolute bottom-0 left-0 w-40 h-40 bg-white opacity-10 rounded-full -ml-20 -mb-20"></div>
                  
                  {/* Content */}
                  <div className="relative z-10">
                    <h2 
                      className="font-bold mb-4 md:mb-6 text-white drop-shadow-lg"
                      style={{ 
                        fontSize: `${template.occasion_title_font_size || 36}px`,
                        lineHeight: '1.2'
                      }}
                    >
                      {template.occasion_title}
                    </h2>
                    <p className="text-lg md:text-xl text-white font-medium leading-relaxed drop-shadow-md">
                      {template.wish_text}
                    </p>
                  </div>
                </div>
                
                <div className="p-5 md:p-6 bg-gradient-to-b from-white to-gray-50">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-3 md:mb-4">{template.name}</h3>
                  <button className="w-full px-6 py-3 md:py-4 bg-gradient-to-r from-[#1E88E5] to-[#1565C0] text-white font-semibold rounded-xl hover:from-[#1565C0] hover:to-[#0D47A1] transition-all duration-300 shadow-lg hover:shadow-xl transform group-hover:scale-105 min-h-[48px]">
                    {t('browse.selectButton')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {templates.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-600 text-lg">{t('browse.noTemplates')}</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BrowseTemplates;